import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as TestIcon,
  VpnKey as KeyIcon,
  DateRange as DateIcon,
} from "@mui/icons-material";
import sellerlogicApi from "../../../../services/api/prepbusiness/sellerlogic";

const SellerlogicCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    display_name: "",
    client_id: "",
    client_secret: "",
    active_omni: false,
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await sellerlogicApi.getAll();
      setCredentials(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Failed to load Sellerlogic credentials:", err);
      setError("Failed to load credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (credential = null) => {
    if (credential) {
      setEditingId(credential.id);
      setFormData({
        display_name: credential.display_name || "",
        client_id: credential.client_id || "",
        client_secret: "", // Don't populate secret
        active_omni: credential.active_omni || false,
      });
    } else {
      setEditingId(null);
      setFormData({
        display_name: "",
        client_id: "",
        client_secret: "",
        active_omni: false,
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.client_id) {
      setError("Client ID is required.");
      return;
    }
    // Secret is required for new, optional for edit
    if (!editingId && !formData.client_secret) {
      setError("Client Secret is required for new credentials.");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Only send fields that have values (handle partial update logic if needed)
        const dataToSend = { ...formData };
        if (!dataToSend.client_secret) {
          delete dataToSend.client_secret;
        }
        await sellerlogicApi.update(editingId, dataToSend);
        setSuccess("Credential updated successfully.");
      } else {
        await sellerlogicApi.create(formData);
        setSuccess("Credential created successfully.");
      }
      handleCloseDialog();
      loadCredentials();
    } catch (err) {
      console.error("Error saving credential:", err);
      setError(err.response?.data?.detail || "Failed to save credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?"))
      return;

    setLoading(true);
    try {
      await sellerlogicApi.delete(id);
      setSuccess("Credential deleted successfully.");
      loadCredentials();
    } catch (err) {
      console.error("Error deleting credential:", err);
      setError("Failed to delete credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id) => {
    setLoading(true);
    try {
      const response = await sellerlogicApi.test(id);
      if (
        response.data?.status === "success" ||
        response.data?.valid === true
      ) {
        setSuccess("Connection successful! Credential is valid.");
      } else {
        setError(
          `Connection failed: ${
            response.data?.detail || "Invalid credentials"
          }`,
        );
      }
    } catch (err) {
      console.error("Test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTestKey = async () => {
    if (!formData.client_id || !formData.client_secret) {
      setError("Please enter Client ID and Client Secret to test.");
      return;
    }

    setLoading(true);
    try {
      const response = await sellerlogicApi.testKey({
        client_id: formData.client_id,
        client_secret: formData.client_secret,
      });

      if (
        response.data?.status === "success" ||
        response.data?.valid === true
      ) {
        setSuccess("Connection successful! Keys are valid.");
      } else {
        setError(
          `Connection failed: ${response.data?.detail || "Invalid keys"}`,
        );
      }
    } catch (err) {
      console.error("Key test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    // Optimistic UI update could be risky if API fails, so we wait.
    try {
      await sellerlogicApi.toggleActive(id);
      loadCredentials(); // Reload list to update other items if exclusivity exists
    } catch (err) {
      console.error("Error toggling active status:", err);
      setError("Failed to toggle active status.");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Sellerlogic Credentials</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Credentials
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How to Get Your Sellerlogic Credentials:</strong>
        </Typography>
        <ol style={{ margin: 0, paddingLeft: "20px" }}>
          <li>Login to Sellerlogic platform</li>
          <li>Confirm 2FA</li>
          <li>
            Obtain your Client ID and Client Secret from application details
          </li>
          <li>Enter the credentials in the form below</li>
        </ol>
        <Typography variant="body2" mt={1}>
          For more information, visit{" "}
          <a
            href="https://sellerlogic.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            SELLERLOGIC API Access
          </a>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {loading && (
        <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
      )}

      {!loading && credentials.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No Sellerlogic credentials found. Add one to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label="sellerlogic credentials table"
          >
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Display Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Client ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Created At</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.map((cred) => (
                <TableRow
                  key={cred.id}
                  hover
                  sx={{
                    bgcolor: cred.active_omni ? "action.selected" : "inherit",
                  }}
                >
                  <TableCell component="th" scope="row">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cred.active_omni || false}
                          onChange={() => handleToggleActive(cred.id)}
                          name="active"
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        cred.active_omni ? (
                          <Chip
                            label="Active"
                            color="primary"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Inactive
                          </Typography>
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <KeyIcon color="action" fontSize="small" />
                      <Typography variant="body1" fontWeight="medium">
                        {cred.display_name || "Unnamed"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {cred.client_id
                      ? `${cred.client_id.substring(0, 8)}...`
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {cred.created_at && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <DateIcon fontSize="small" color="action" />
                        {new Date(cred.created_at).toLocaleDateString()}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test Connection">
                      <IconButton
                        size="small"
                        onClick={() => handleTest(cred.id)}
                        color="info"
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(cred)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(cred.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? "Edit Sellerlogic Credential"
            : "Add Sellerlogic Credential"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Display Name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              placeholder="My Sellerlogic Account"
              helperText="A friendly name to identify this account"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Client ID"
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required={!editingId}
              fullWidth
              label="Client Secret"
              name="client_secret"
              type="password"
              value={formData.client_secret}
              onChange={handleChange}
              placeholder={editingId ? "Leave blank to keep unchanged" : ""}
              helperText={
                editingId
                  ? "Only enter if you want to update the secret"
                  : "Required for new accounts"
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestKey}
            color="info"
            disabled={loading || !formData.client_id || !formData.client_secret}
          >
            Test Keys
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerlogicCredentials;
